/**
 * Toast Component
 *
 * Notification toast with auto-dismiss, animations, and multiple types.
 * Positioned at top-right corner with slide-in/out animations.
 *
 * CRITICAL CONSTRAINTS:
 * - Tailwind CSS core utilities only (NO custom compilation)
 * - TypeScript strict mode (NO 'any' types)
 * - Smooth 300ms transitions
 * - User-friendly messages
 */

import React, { useState, useEffect } from 'react';
import { useToast, type Toast as ToastType } from '@/contexts/ToastContext';

interface SingleToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

/**
 * SingleToast Component
 *
 * Individual toast notification with animations
 */
function SingleToast({ toast, onDismiss }: SingleToastProps): React.JSX.Element {
  const [isExiting, setIsExiting] = useState<boolean>(false);

  useEffect(() => {
    // If persistent, do not auto-dismiss
    if (toast.persistent) {
      return;
    }

    // Trigger exit animation before removal
    const exitTimer = setTimeout(() => {
      setIsExiting(true);
      // Allow animation to complete before dismissing
      setTimeout(() => {
        onDismiss(toast.id);
      }, 300);
    }, toast.duration - 300);

    return () => clearTimeout(exitTimer);
  }, [toast.duration, toast.id, onDismiss, toast.persistent]);

  // Type-specific classes
  const typeClasses: Record<string, string> = {
    success: 'bg-green-50 border-green-400 text-green-700',
    error: 'bg-red-50 border-red-400 text-red-700',
    info: 'bg-blue-50 border-blue-400 text-blue-700',
  };

  // Animation classes
  const animationClasses = isExiting
    ? 'translate-x-full opacity-0'
    : 'translate-x-0 opacity-100';

  return (
    <div
      className={`flex items-start p-4 mb-3 border rounded-lg shadow-lg transition-all duration-300 ${typeClasses[toast.type]} ${animationClasses}`}
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
    >
      {/* Icon */}
      <div className="flex-shrink-0">
        {toast.type === 'success' && (
          <svg
            className="h-5 w-5 text-green-400"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
        )}
        {toast.type === 'error' && (
          <svg
            className="h-5 w-5 text-red-400"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        )}
        {toast.type === 'info' && (
          <svg
            className="h-5 w-5 text-blue-400"
            fill="currentColor"
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>

      {/* Message */}
      <div className="ml-3 flex-1">
        <p className="text-sm font-medium">{toast.message}</p>
      </div>

      {/* Close Button */}
      <button
        onClick={() => {
          setIsExiting(true);
          setTimeout(() => onDismiss(toast.id), 300);
        }}
        className="ml-4 flex-shrink-0 inline-flex text-current hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current transition-opacity duration-200"
        aria-label="Dismiss notification"
      >
        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
}

/**
 * ToastContainer Component
 *
 * Container for all toasts, positioned at top-right
 * Integrates with ToastContext
 */
export function ToastContainer(): React.JSX.Element {
  const { toasts, dismissToast } = useToast();

  if (toasts.length === 0) {
    return <></>;
  }

  return (
    <div
      className="fixed top-4 right-4 z-50 w-full max-w-sm pointer-events-none"
      aria-live="polite"
      aria-atomic="false"
    >
      <div className="pointer-events-auto">
        {toasts.map((toast) => (
          <SingleToast key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </div>
  );
}
